import { useState } from 'react';
import {
  BUG_SEVERITIES,
  MAX_SUBJECT_LENGTH,
  MIN_DESCRIPTION_LENGTH,
  MIN_SUBJECT_LENGTH,
  useCreateBugReportMutation,
} from 'app/services/bugReportApi';
import type { IBugReportCreated } from 'app/services/bugReportApi';
import {
  AreaField,
  AttachmentField,
  DiscardConfirm,
  EmailField,
  FeedbackContext,
  FeedbackDone,
  MinLengthHint,
  autoGrow,
  defaultArea,
  feedbackErrorMessage,
  useAttachments,
  useCloseGuard,
} from './FeedbackForm';

/**
 * "Report a Bug" popup, opened from the header's More Actions menu
 * (GlobalHeader) and therefore reachable from every SPA page.
 *
 * The form asks for six things and captures the rest itself — page URL,
 * browser, viewport, build versions, and the project/AHU id when the route has
 * one (see `collectContext` / `appendCapturedContext` in `app/services/bugReportApi.ts`).
 * That is deliberate: the fields a reporter forgets are exactly the ones triage
 * needs, and asking for them is how a form stops getting used. The strip under
 * the heading shows the two the reporter would otherwise wonder about — which
 * page this is about, and which build it is — so the form visibly stands for
 * what it sends.
 *
 * Submission is a single POST that queues the report for the central pipeline,
 * which stores it and emails support. Success shows a short reference to quote;
 * a failure (503) keeps everything the reporter typed so they can retry.
 *
 * Email Address is SHOWN, not asked for: see `EmailField` in
 * `FeedbackForm.tsx` on why the destination is not something the form lets
 * anyone choose.
 *
 * The parts Request a Feature (`FeatureRequestPopup.tsx`) shares — context
 * strip, Your email, Area, the file row, the discard confirmation, the thank-you
 * card — live in `FeedbackForm.tsx`; this file keeps what is particular to a
 * bug: Severity, What happened?, Steps to reproduce, and the wording.
 *
 * ★ Styling: the markup carries NO inline styles and reuses the app's own form
 * chrome — `.popup-wrapper` / `.popup-content` / `.popup-heading` /
 * `.popup-content-part` / `.popup-control-buttons` for the shell, and `.label` /
 * `.required` for each row, exactly as EditProfilePopup does. The shape of the
 * dialog (680px card, heading, read-only context strip, form, footer) follows
 * the Add Section popup — `pages/ahu-board/components/InsertSectionPopup.tsx` —
 * which is the house reference for a popup that asks for something.
 * `_ui.input.scss` gives inputs the underline treatment, the #2196f3 focus
 * colour and the `.required:after` asterisk, so a hand-rolled bordered field
 * would be the thing that looked foreign. The layout rules live in
 * `django/static/css/common/sass/ui/popup/_feedback_form.scss` — edit the SCSS and
 * re-run `npm run css` in `django/`, never `common.css` directly (CLAUDE.md,
 * "Shared legacy CSS").
 *
 * Esc comes from the shared popup convention: the global body handler
 * (`usePopupKeys`) CLICKS `button[name="cancel"]` in the topmost
 * `.popup-wrapper.active`, which is why Cancel carries that name — and why
 * routing Cancel through `requestClose` covers Esc for free.
 *
 * ⚠ That handler is mounted per PAGE, and four pages that render GlobalHeader
 * never mount it: Partners, KAI Select, CMS Admin and the CAD viewer. Esc
 * therefore does nothing to this popup there. Not worth fixing here — Cancel
 * works everywhere — but it is why Esc must not be the only way out.
 */

interface BugReportPopupProps {
  onClose: () => void;
  /** The page the reporter is on — GlobalHeader's `appName`, which is also the
   *  server's `area` value, so it pre-selects the Area field. */
  appName?: string;
}

const ERRORS = {
  generic: 'The report could not be sent. Please try again, or email support directly.',
  tooMany: 'Too many reports in the last hour. Please try again later.',
};

const BugReportPopup = ({ onClose, appName }: BugReportPopupProps) => {
  const [createBugReport, { isLoading }] = useCreateBugReportMutation();

  const [area, setArea] = useState(() => defaultArea(appName));
  const [severity, setSeverity] = useState('major');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [reqError, setReqError] = useState('');
  const [created, setCreated] = useState<IBugReportCreated | null>(null);
  const file = useAttachments(setReqError);

  // Mirrors the server's own minimums so Submit is blocked before a round trip
  // (api/rest_bug_report/serializers.py). Title and What happened? are the only
  // two gates: Area and Severity always hold a value, and everything else on the
  // form is optional, so Send Report lights up as soon as these two are long
  // enough — nothing further down the form can hold it back.
  const subjectLength = subject.trim().length;
  const descriptionLength = description.trim().length;
  const canSubmit =
    subjectLength >= MIN_SUBJECT_LENGTH &&
    descriptionLength >= MIN_DESCRIPTION_LENGTH &&
    !isLoading;

  const hasDraft = Boolean(
    subject.trim() || description.trim() || steps.trim() || file.attachments.length
  );
  const { confirmDiscard, requestClose, keepEditing, discard } = useCloseGuard({
    isLoading,
    hasDraft,
    done: Boolean(created),
    onClose,
  });

  const onSubmit = async () => {
    if (!canSubmit) return;
    setReqError('');
    try {
      const report = await createBugReport({
        subject,
        description,
        steps,
        severity,
        area,
        attachments: file.attachments,
      }).unwrap();
      setCreated(report);
    } catch (error) {
      setReqError(feedbackErrorMessage(error, ERRORS));
    }
  };

  if (created) {
    return (
      <FeedbackDone title="Thanks — report sent" onClose={onClose}>
        <p>
          Kinetix Support has your report. If you follow up, quote Support Reference{' '}
          <strong>{created.reference}</strong>.
        </p>
      </FeedbackDone>
    );
  }

  return (
    <>
      <div id="popup-content" className="popup-wrapper feedback-popup active">
        <div className="popup-content">
          {/* Heading text in its own span so the h2's `space-between` has two
              flex items to push apart, as the Add Section popup's does. */}
          <h2 className="popup-heading">
            <span className="popup-heading-text">Report a Bug</span>
            <span className="popup-heading-note">Emailed to Kinetix Support</span>
          </h2>
          {/* The reference popup opens with a read-only block saying what is
              being acted on; here that is the page the report will carry and
              the build it was seen on. */}
          <FeedbackContext />
          <div className="popup-content-part feedback-fields">
            <EmailField
              id="bug_report_email"
              hint="Your account address. Your receipt and support's reply come here."
              noEmailHint="Your account has no email address, so support can only reply through the report."
            />

            <AreaField id="bug_report_area" value={area} onChange={setArea} />

            <div className="feedback-field">
              <label className="label" htmlFor="bug_report_severity">
                Severity
              </label>
              <div className="feedback-control">
                <select
                  id="bug_report_severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                >
                  {BUG_SEVERITIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="feedback-field">
              <label className="label required" htmlFor="bug_report_subject">
                Title
              </label>
              <div className="feedback-control">
                <input
                  id="bug_report_subject"
                  type="text"
                  autoFocus
                  maxLength={MAX_SUBJECT_LENGTH}
                  placeholder="Coil rating comes back as 0 CFM"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <MinLengthHint min={MIN_SUBJECT_LENGTH} length={subjectLength} />
              </div>
            </div>

            <div className="feedback-field">
              <label className="label required" htmlFor="bug_report_description">
                What happened?
              </label>
              <div className="feedback-control">
                {/* Two rows, not four: one sentence in a four-row box left a
                    visible well of empty space under what the reporter had
                    written. `autoGrow` adds the rows back as they are typed, so
                    nothing is lost by opening shallow — and
                    `.feedback-compact-text` lowers the SCSS floor to match, or
                    `min-height` would hold the old four rows open regardless of
                    `rows`. */}
                <textarea
                  id="bug_report_description"
                  className="feedback-compact-text"
                  ref={autoGrow}
                  rows={2}
                  placeholder="What you expected, and what you got instead."
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    autoGrow(e.target);
                  }}
                />
                <MinLengthHint min={MIN_DESCRIPTION_LENGTH} length={descriptionLength} />
              </div>
            </div>

            <div className="feedback-field">
              <label className="label" htmlFor="bug_report_steps">
                Steps to reproduce
              </label>
              <div className="feedback-control">
                {/* Four rows, not three: the three-step example below fills a
                    three-row box exactly, so the last line sat on the field's
                    own underline. */}
                <textarea
                  id="bug_report_steps"
                  ref={autoGrow}
                  rows={4}
                  placeholder={'1. Open AHU 1234\n2. Set the cooling coil to 4 row\n3. Click Rate'}
                  value={steps}
                  onChange={(e) => {
                    setSteps(e.target.value);
                    autoGrow(e.target);
                  }}
                />
              </div>
            </div>

            <AttachmentField
              id="bug_report_attachment"
              label="Screenshots or recordings"
              attachments={file.attachments}
              fileInputRef={file.fileInputRef}
              onPick={file.pick}
              onRemove={file.remove}
            />
          </div>
          <div className="popup-control-buttons">
            <span className="popup-note">* Required field</span>
            {reqError && <div className="req-error">{reqError}</div>}
            {/* No autoFocus here. It used to start focus on Send Report, which on
                an empty form is `non-clickable` (pointer-events:none, opacity .5)
                but not `disabled` — so a keyboard user landed on a button that
                looks dead and had to Shift+Tab back through every field. Focus
                starts on Title instead, the first thing anyone types. */}
            <button
              className={`btn highlighted-btn${canSubmit ? '' : ' non-clickable'}`}
              type="button"
              onClick={onSubmit}
            >
              {isLoading ? 'Sending…' : 'Send Report'}
            </button>
            {/* `requestClose`, not `onClose`: this is also what Esc clicks. */}
            <button
              className={`btn not-highlighted-btn${isLoading ? ' non-clickable' : ''}`}
              type="button"
              name="cancel"
              onClick={requestClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      {confirmDiscard && (
        <DiscardConfirm title="Discard this bug report?" onKeep={keepEditing} onDiscard={discard}>
          <p>What you have written will be lost. Nothing has been sent to support yet.</p>
        </DiscardConfirm>
      )}
    </>
  );
};

export default BugReportPopup;
